// Pure search logic for the cup collection. No DOM, no Three.js — just
// takes a query and a cup list, returns scored matches.
//
// The matching strategy is case-insensitive substring across the fields
// users actually search by: name (the attraction), city, country,
// continent (derived from country via COUNTRY_CONTINENTS below), series,
// and notes. Per-field weights bias the dropdown ordering so a cup
// whose name matches outranks one whose notes mention the same word
// incidentally.
//
// 12 cups today; substring is fine. Bump to a fuzzy match (e.g.
// fuse.js) once the collection is big enough that "tok" matches Tokyo
// but not "took the cup home" in notes.

import type { Cup } from "./types";

export type MatchedField =
  | "name"
  | "city"
  | "country"
  | "continent"
  | "series"
  | "notes";

export interface SearchMatch {
  cup: Cup;
  score: number;
  /** Which field's substring caused the highest-scoring match. Used by
   * the dropdown UI to underline / display "matched on city: Paris". */
  matchedField: MatchedField;
}

const FIELD_WEIGHTS: Record<MatchedField, number> = {
  name: 5,
  city: 4,
  country: 3,
  continent: 3,
  series: 2,
  notes: 1,
};

// Country → list of continent labels that should match a search for that
// cup. Multiple labels per country handle aliases ("America" for USA,
// "Australia" for the country-named-as-continent) and transcontinental
// cases (Türkiye spans Asia + Europe; UAE often gets searched as
// "Middle East"). Search is substring + case-insensitive against these
// strings, so "asi" matches "Asia" and "amer" matches "America".
//
// When new countries are added to cups.json, extend this map.
const COUNTRY_CONTINENTS: Record<string, string[]> = {
  Australia: ["Oceania", "Australia"],
  France: ["Europe"],
  Germany: ["Europe"],
  Italy: ["Europe"],
  Japan: ["Asia"],
  Singapore: ["Asia"],
  Türkiye: ["Asia", "Europe", "Middle East"],
  UAE: ["Asia", "Middle East"],
  UK: ["Europe"],
  USA: ["North America", "America"],
};

export function searchCups(cups: Cup[], query: string): SearchMatch[] {
  const q = query.trim().toLowerCase();
  if (q === "") return [];

  const matches: SearchMatch[] = [];
  for (const cup of cups) {
    let bestScore = 0;
    let bestField: MatchedField | null = null;

    // Score = field weight + small bonus for early position (so "Paris"
    // picks Paris-named cups over those with "Paris" deep in notes).
    // Cap the position bonus so it doesn't dominate the field weight.
    function consider(field: MatchedField, value: string): void {
      const idx = value.toLowerCase().indexOf(q);
      if (idx === -1) return;
      const positionBonus = Math.max(0, 1 - idx / 30);
      const score = FIELD_WEIGHTS[field] + positionBonus;
      if (score > bestScore) {
        bestScore = score;
        bestField = field;
      }
    }

    consider("name", cup.name);
    consider("city", cup.city);
    consider("country", cup.country);
    consider("series", cup.series);
    if (cup.notes) consider("notes", cup.notes);

    // Continent is virtual — derived from the cup's country via the
    // COUNTRY_CONTINENTS map. Try each continent label; keep the best.
    const continents = COUNTRY_CONTINENTS[cup.country] ?? [];
    for (const c of continents) consider("continent", c);

    if (bestField !== null) {
      matches.push({ cup, score: bestScore, matchedField: bestField });
    }
  }

  matches.sort((a, b) => b.score - a.score);
  return matches;
}
